import {
  getLowestPrice,
  getPrice,
  getFirstAttributeValue,
} from '@scayle/storefront-nuxt'

import type {
  TrackAddToWishListParams,
  TrackRemoveFromWishListParams,
  TrackingEvent,
  TrackingPayload,
  TrackWishListParams,
  EcommerceItem,
} from '~/types/tracking'
import { getCategoryLevels, getSizeKey } from '~/utils/tracking'
import { getSizeLabel } from '#layers/base/types/variant'

const useWishlistEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => any,
) => {
  const currentShop = useCurrentShop().value
  const { getStoredProductTrackingItem } = useProductTrackingLocalStorage()

  return {
    trackRemoveFromWishlist: ({
      product,
      variant,
    }: TrackRemoveFromWishListParams) => {
      const categoryLevels = getCategoryLevels(product.categories)
      const storedProductTracking = getStoredProductTrackingItem(
        String(product.referenceKey),
      )

      const price = variant
        ? getPrice(variant)
        : getLowestPrice(product.variants || [])

      const items: EcommerceItem[] = [
        {
          item_id: product.id.toString(),
          item_sku: variant?.referenceKey || product.referenceKey || '',
          item_name:
            getFirstAttributeValue(product.attributes, 'name')?.label || '',
          item_brand:
            getFirstAttributeValue(product.attributes, 'brand')?.label || '',
          item_variant:
            getFirstAttributeValue(product.attributes, 'color')?.label || '',
          item_size: variant ? getSizeLabel(variant) : '',
          item_size_key: variant?.referenceKey
            ? getSizeKey(variant.referenceKey) || ''
            : '',
          item_category: categoryLevels.item_category || '',
          item_category2: categoryLevels.item_category2 || '',
          item_category3: categoryLevels.item_category3 || '',
          price: toFloat(price.withTax),
          coupon: '',
          quantity: 1,
          index: storedProductTracking?.index ?? 0,
          stock: variant?.stock.quantity || 0,
          ordertype: 'shop',
          item_list_name: storedProductTracking?.item_list_name || '',
          item_list_type: storedProductTracking?.item_list_type || '',
        },
      ]

      track('remove_from_wishlist', {
        ecommerce: {
          currency: currentShop?.currency,
          value: toFloat(price.withTax),
          net_value: toFloat(price.withoutTax),
          items,
        },
      })
    },

    trackAddToWishlist: ({
      product,
      variant,
      listType,
      listName,
      index,
      type,
    }: TrackAddToWishListParams) => {
      const categoryLevels = getCategoryLevels(product.categories)
      const storedProductTracking = getStoredProductTrackingItem(
        String(product.referenceKey),
      )

      const price = variant
        ? getPrice(variant)
        : getLowestPrice(product.variants || [])

      const items: EcommerceItem[] = [
        {
          item_id: product.id.toString(),
          item_sku: variant?.referenceKey || product.referenceKey || '',
          item_name:
            getFirstAttributeValue(product.attributes, 'name')?.label || '',
          item_brand:
            getFirstAttributeValue(product.attributes, 'brand')?.label || '',
          item_variant:
            getFirstAttributeValue(product.attributes, 'color')?.label || '',
          item_size: variant ? getSizeLabel(variant) : '',
          item_size_key: variant?.referenceKey
            ? getSizeKey(variant.referenceKey) || ''
            : '',
          item_category: categoryLevels.item_category || '',
          item_category2: categoryLevels.item_category2 || '',
          item_category3: categoryLevels.item_category3 || '',
          price: toFloat(price.withTax),
          coupon: '',
          quantity: 1,
          index: (index ?? 0) + 1,
          stock: variant?.stock.quantity || 0,
          ordertype: 'shop',
          item_list_name:
            listName || storedProductTracking?.item_list_name || '',
          item_list_type:
            listType || storedProductTracking?.item_list_type || '',
        },
      ]

      track('add_to_wishlist', {
        add_to_wishlist_type: type,
        ecommerce: {
          currency: currentShop?.currency,
          value: toFloat(price.withTax),
          net_value: toFloat(price.withoutTax),
          items,
        },
      })
    },

    trackWishlist: ({ wishlistItems }: TrackWishListParams) => {
      const items: EcommerceItem[] = wishlistItems.map(
        ({ product, variant }) => {
          const categoryLevels = getCategoryLevels(product.categories)
          const storedProductTracking = getStoredProductTrackingItem(
            String(product.referenceKey),
          )

          const price = variant
            ? getPrice(variant)
            : getLowestPrice(product.variants || [])

          return {
            item_id: product.id.toString(),
            item_sku: variant?.referenceKey || product.referenceKey || '',
            item_name:
              getFirstAttributeValue(product.attributes, 'name')?.label || '',
            item_brand:
              getFirstAttributeValue(product.attributes, 'brand')?.label || '',
            item_variant:
              getFirstAttributeValue(product.attributes, 'color')?.label || '',
            item_size: variant ? getSizeLabel(variant) : '',
            item_size_key: variant?.referenceKey
              ? getSizeKey(variant.referenceKey) || ''
              : '',
            item_category: categoryLevels.item_category || '',
            item_category2: categoryLevels.item_category2 || '',
            item_category3: categoryLevels.item_category3 || '',
            price: toFloat(price.withTax),
            coupon: '',
            quantity: 1,
            index: storedProductTracking?.index ?? 0,
            stock: variant?.stock.quantity || 0,
            ordertype: 'shop',
            item_list_name: storedProductTracking?.item_list_name || '',
            item_list_type: storedProductTracking?.item_list_type || '',
          }
        },
      )

      const prices = wishlistItems.map(({ product, variant }) => {
        return variant
          ? getPrice(variant)
          : getLowestPrice(product.variants || [])
      })

      track('view_wishlist', {
        ecommerce: {
          value: toFloat(prices.reduce((acc, price) => acc + price.withTax, 0)),
          net_value: toFloat(
            prices.reduce((acc, price) => acc + price.withoutTax, 0),
          ),
          items,
        },
      })
    },
  }
}
export default useWishlistEvents
